import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";
import { listMyMemberships } from "@/lib/supabase/memberships";
import { listOrgProperties } from "@/lib/supabase/properties";
import type {
  Organization,
  Membership,
  MembershipWithOrg,
  MembershipRole,
  Property,
  PropertyType,
} from "@/lib/supabase/types";

/**
 * Sprint 0 — AuthContext now carries tenant state in addition to the
 * Supabase auth user/session.
 *
 * After the user signs in we fetch their memberships (RLS returns only
 * the orgs they belong to). We pick one as `currentOrg` — the first
 * one in the list is the default, the user can switch via future UI.
 * Role from that membership is exposed so RBAC checks stay close to
 * the component that needs them.
 *
 * Consumers that used to just read `user`/`session` keep working — we
 * only added fields, never removed.
 */

interface AuthContextType {
  // Supabase auth
  session: Session | null;
  user: User | null;
  loading: boolean;

  // Tenant state
  memberships: MembershipWithOrg[];
  currentOrg: Organization | null;
  currentMembership: Membership | null;
  currentRole: MembershipRole | null;
  /** The current org's properties — derived from this is the module set */
  currentProperties: Property[];
  /** The set of modules enabled for the current org, computed as the
   *  union of the types of its properties. Nav rendering and route
   *  guards read from this instead of hard-coding the full menu. */
  modules: Set<PropertyType>;
  /** Convenience booleans — same data as `modules`, easier at call sites */
  hasHotel: boolean;
  hasRental: boolean;
  /** True while we're fetching memberships/properties post-sign-in */
  orgLoading: boolean;
  /** True once memberships and the current org's properties have both
   *  loaded — until then `modules` / `hasHotel` / `hasRental` are not
   *  final, so module guards must wait instead of redirecting. */
  modulesReady: boolean;
  /** Set to true when the user has no active membership */
  hasNoOrg: boolean;

  // Actions
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  switchOrg: (orgId: string) => void;
  refreshMemberships: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [memberships, setMemberships] = useState<MembershipWithOrg[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [currentProperties, setCurrentProperties] = useState<Property[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  // Org id whose properties are currently in `currentProperties`
  // (undefined = never loaded).
  const [propertiesOrgId, setPropertiesOrgId] = useState<string | null | undefined>(undefined);

  const loadMemberships = useCallback(async () => {
    setOrgLoading(true);
    try {
      const rows = await listMyMemberships();
      setMemberships(rows);
      setCurrentOrgId((prev) => {
        if (prev && rows.some((r) => r.org_id === prev)) return prev;
        return rows[0]?.org_id ?? null;
      });
    } catch (e) {
      console.warn("[AuthContext] memberships fetch failed", e);
      setMemberships([]);
    } finally {
      setOrgLoading(false);
      setMembershipsLoaded(true);
    }
  }, []);

  // Load the current org's properties — the source of truth for which
  // feature modules (hotel / rental) the dashboard should show.
  const loadProperties = useCallback(async (orgId: string | null) => {
    if (!orgId) {
      setCurrentProperties([]);
      setPropertiesOrgId(null);
      return;
    }
    try {
      const rows = await listOrgProperties(orgId);
      setCurrentProperties(rows);
    } catch (e) {
      console.warn("[AuthContext] properties fetch failed", e);
      setCurrentProperties([]);
    } finally {
      setPropertiesOrgId(orgId);
    }
  }, []);

  // Bootstrap session + listen for auth changes.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Whenever we have an authenticated user, refresh memberships.
  useEffect(() => {
    if (user) {
      loadMemberships();
    } else {
      setMemberships([]);
      setMembershipsLoaded(false);
      setCurrentOrgId(null);
      setCurrentProperties([]);
    }
  }, [user, loadMemberships]);

  // Whenever the current org changes, refresh its properties.
  useEffect(() => {
    loadProperties(currentOrgId);
  }, [currentOrgId, loadProperties]);

  const signIn = async (email: string, password: string, rememberMe: boolean = true): Promise<{ error: string | null }> => {
    // Sprint 9c — persist the remember-me flag BEFORE sign-in so the
    // token store adapter picks the right backend (localStorage if
    // remembered, sessionStorage if not).
    const { setRememberMe } = await import("@/lib/passwordPolicy");
    setRememberMe(rememberMe);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
  ): Promise<{ error: string | null }> => {
    // Sprint 9c — enforce password policy on the client before hitting
    // Supabase. Supabase has its own min-length but nothing on complexity.
    const { checkPassword } = await import("@/lib/passwordPolicy");
    const policy = checkPassword(password, email);
    if (!policy.ok) return { error: policy.error ?? "Password does not meet requirements" };

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMemberships([]);
    setCurrentOrgId(null);
  };

  const switchOrg = (orgId: string) => {
    if (memberships.some((m) => m.org_id === orgId)) {
      setCurrentOrgId(orgId);
    }
  };

  const currentMembership = currentOrgId
    ? memberships.find((m) => m.org_id === currentOrgId) ?? null
    : null;
  const currentOrg = currentMembership?.organization ?? null;
  const currentRole = currentMembership?.role ?? null;

  // Sprint 9b — tag Sentry events with the active user + org so we can
  // correlate errors in production without shipping PII.
  useEffect(() => {
    if (user && currentOrg && currentRole) {
      import("@/lib/sentry").then((s) => s.identifySentryUser(user.id, currentOrg.id, currentRole));
    } else {
      import("@/lib/sentry").then((s) => s.clearSentryUser());
    }
  }, [user, currentOrg, currentRole]);
  const hasNoOrg = !loading && !!user && !orgLoading && memberships.length === 0;

  // Module set — union of property types on the current org. If the
  // org has both a hotel and a rental property, both modules are on.
  // While properties are loading we return an empty set; pages with
  // route guards should show a loader in that window, not redirect.
  const modules = new Set<PropertyType>(
    currentProperties.map((p) => p.type),
  );
  const hasHotel = modules.has("hotel");
  const hasRental = modules.has("rental");
  const modulesReady = membershipsLoaded && !orgLoading && propertiesOrgId === currentOrgId;

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        memberships,
        currentOrg,
        currentMembership,
        currentRole,
        currentProperties,
        modules,
        hasHotel,
        hasRental,
        orgLoading,
        modulesReady,
        hasNoOrg,
        signIn,
        signUp,
        signOut,
        switchOrg,
        refreshMemberships: loadMemberships,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
